import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { mutateCart, BUNDLE_GIFT_PROPERTY } from '@theme/cart-extras';

/**
 * Purchase option cards (subscription plans / bundles) that drive the product form rendered by
 * the buy-buttons block. Selecting a card writes the variant id, quantity and selling plan into
 * that form, so the theme's own add-to-cart flow submits the right line item.
 *
 * With the "Subscribe & save" checkbox unticked, the same cards add one-time purchases (no selling plan),
 * and `data-mode` switches the prices the cards show. After the product form adds a card, the gift
 * products in that card (`[data-gift-variant-id]`, from "_purchase-gift" blocks) are added as free lines.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement[]} options - Radio inputs, one per card.
 * @property {HTMLInputElement} sellingPlan - Hidden selling_plan input associated with the product form.
 * @property {HTMLInputElement} [subscribe] - "Subscribe & save" checkbox.
 * @property {HTMLButtonElement | HTMLInputElement} [oneTime] - "One-time purchase" link, or the "Buy once" radio card.
 *
 * @extends Component<Refs>
 */
class PurchaseOptionsComponent extends Component {
  requiredRefs = ['options', 'sellingPlan'];

  /** @type {AbortController | null} */
  #controller = null;

  /** True while the one-time purchase link is the selection instead of a card. */
  #oneTimeSelected = false;

  /** True while addOneTime submits the form, so that add doesn't earn the selected card's gifts. */
  #addingOneTime = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, {
      signal: this.#controller.signal,
    });
    this.#ensureSelection();
    // Wait a frame so the product form (rendered later in the details column) exists.
    requestAnimationFrame(() => this.#apply());
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setOneTime(this.#oneTimeSelected);
    this.#ensureSelection();
    this.#syncMode();
    this.#apply();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** Called when a card's radio changes. */
  select() {
    this.#setOneTime(false);
    this.#apply();
  }

  /**
   * Called by the one-time purchase link: a plain one-time line instead of a plan. With data-action="add" it goes
   * straight to the cart (the product form then opens the cart drawer); otherwise it becomes the selection.
   */
  selectOneTime() {
    const { oneTime } = this.refs;
    if (oneTime?.dataset.action === 'add') {
      this.addOneTime({ variantId: oneTime.dataset.variantId ?? '', quantity: Number(oneTime.dataset.quantity) || 1 });
      return;
    }
    for (const input of this.refs.options) input.checked = false;
    this.#setOneTime(true);
    this.#apply();
  }

  /**
   * Opens or closes a welcome kit's item list.
   * @param {Event} event
   */
  toggleKit(event) {
    const button = event.target instanceof Element ? event.target.closest('.purchase-option__kit-toggle') : null;
    const kit = button?.closest('.purchase-option__kit');
    if (!button || !kit) return;
    const open = kit.classList.toggle('is-open');
    button.setAttribute('aria-expanded', String(open));
  }

  /** @param {boolean} selected */
  #setOneTime(selected) {
    this.#oneTimeSelected = selected;
    const { oneTime } = this.refs;
    if (!oneTime) return;
    // Card style: a radio in the plans' group. Link style: a toggle button, unless it adds to cart right away.
    if (oneTime instanceof HTMLInputElement) oneTime.checked = selected;
    else if (oneTime.dataset.action !== 'add') oneTime.setAttribute('aria-pressed', String(selected));
  }

  /** Called when the "Subscribe & save" checkbox changes. */
  toggleSubscribe() {
    this.#syncMode();
    this.#apply();
  }

  get #subscribed() {
    return this.refs.subscribe ? this.refs.subscribe.checked : true;
  }

  #syncMode() {
    this.dataset.mode = this.#subscribed ? 'subscription' : 'onetime';
  }

  /**
   * Adds the selected card's gift products once the product form in this section has added that card.
   * @param {Event} event
   */
  #onCartLinesUpdate = async (event) => {
    if (!(event instanceof CartLinesUpdateEvent) || event.action !== 'add' || event.context !== 'product') return;
    const source = event.target;
    if (!(source instanceof Element) || !source.closest('product-form-component')) return;
    if (source.closest('.shopify-section') !== this.closest('.shopify-section')) return;
    if (this.#addingOneTime) return;

    const selected = this.#selected;
    if (!selected) return;
    const units = Number(selected.dataset.units) || Number(selected.dataset.quantity) || 1;
    const mode = this.dataset.mode;
    const gifts = [...(selected.closest('.purchase-option')?.querySelectorAll('[data-gift-variant-id]') ?? [])]
      // Subscriber-only gifts (data-show="subscription") don't come with one-time purchases.
      .filter((element) => element instanceof HTMLElement && (!element.dataset.show || element.dataset.show === mode))
      .map((element) => (element instanceof HTMLElement ? Number(element.dataset.giftVariantId) : 0))
      .filter(Boolean)
      .map((id) => ({ id, min: units }));
    // Only the card's own add (not the one-time link) earns its gifts.
    if (!gifts.length || event.lines[0]?.quantity !== Number(selected.dataset.quantity)) return;

    try {
      const result = await event.promise;
      if (result?.detail?.didError) return;
      /** @type {{ items: Array<{ variant_id: number, properties: Record<string, string> | null }> }} */
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
      const missing = gifts.filter(
        (gift) => !cart.items.some((item) => item.variant_id === gift.id && item.properties?.[BUNDLE_GIFT_PROPERTY])
      );
      if (!missing.length) return;
      await mutateCart(
        this,
        'add',
        { items: missing.map((gift) => ({ id: gift.id, quantity: 1, properties: { [BUNDLE_GIFT_PROPERTY]: String(gift.min) } })) },
        { action: 'add', lines: missing.map((gift) => ({ merchandiseId: String(gift.id), quantity: 1 })) },
        'bundle-gift'
      );
    } catch (error) {
      console.error('[purchase-options] gift', error);
    }
  };

  /**
   * Adds the one-time purchase (no plan, quantity 1, base variant) straight to the cart.
   * Used by the "One-time purchase" block and the one-time link (data-action="add").
   * @param {{ variantId: string, quantity?: number }} oneTime
   */
  addOneTime({ variantId, quantity = 1 }) {
    const form = this.#form;
    if (!form) return;
    this.#write({ variantId, quantity, sellingPlanId: '' });
    // The product form dispatches its cart event synchronously inside the submit.
    this.#addingOneTime = true;
    try {
      form.requestSubmit();
    } finally {
      this.#addingOneTime = false;
    }
    // Restore the selected card so the visible state and the form agree again.
    requestAnimationFrame(() => this.#apply());
  }

  get #form() {
    const id = this.dataset.formId;
    const form = id ? document.getElementById(id) : null;
    return form instanceof HTMLFormElement ? form : null;
  }

  get #selected() {
    return this.refs.options.find((input) => input.checked) ?? null;
  }

  #ensureSelection() {
    if (!this.#oneTimeSelected && !this.#selected && this.refs.options[0]) this.refs.options[0].checked = true;
  }

  /**
   * The selected offer as its card shows it, for components that repeat it (the sticky add-to-cart bar).
   * Null when the selection has no card, e.g. the one-time link.
   * @returns {{ title: string, price: string } | null}
   */
  get selection() {
    const input = this.#oneTimeSelected ? this.refs.oneTime : this.#selected;
    const modes = [...(input?.closest('.purchase-option')?.querySelectorAll('.purchase-option__mode') ?? [])];
    const mode = modes.find((element) => {
      return element instanceof HTMLElement && (!element.dataset.show || element.dataset.show === this.dataset.mode);
    });
    if (!mode) return null;
    const text = (/** @type {string} */ selector) => mode.querySelector(selector)?.textContent?.trim() ?? '';
    const title = text('.purchase-option__title');
    const amount = text('.purchase-option__amount');
    return title && amount ? { title, price: `${amount}${text('.purchase-option__suffix')}` } : null;
  }

  #apply() {
    const { oneTime } = this.refs;
    const selected = this.#selected;
    if (this.#oneTimeSelected && oneTime) {
      this.#write({
        variantId: oneTime.dataset.variantId ?? '',
        quantity: Number(oneTime.dataset.quantity) || 1,
        sellingPlanId: '',
      });
    } else if (selected) {
      this.#write({
        variantId: selected.dataset.variantId ?? '',
        quantity: Number(selected.dataset.quantity) || 1,
        sellingPlanId: this.#subscribed ? selected.dataset.sellingPlanId ?? '' : '',
      });
    } else {
      return;
    }
    this.dispatchEvent(new CustomEvent(ThemeEvents.purchaseOptionChange, { bubbles: true, detail: this.selection }));
  }

  /** @param {{ variantId: string, quantity: number, sellingPlanId: string }} line */
  #write({ variantId, quantity, sellingPlanId }) {
    const form = this.#form;
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantId) variantInput.value = variantId;

    const quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(quantity);
    } else {
      // No quantity selector rendered: provide one through the form association.
      let fallback = form.querySelector('input[data-purchase-options-quantity]');
      if (!(fallback instanceof HTMLInputElement)) {
        fallback = document.createElement('input');
        fallback.type = 'hidden';
        fallback.name = 'quantity';
        fallback.dataset.purchaseOptionsQuantity = '';
        form.append(fallback);
      }
      fallback.value = String(quantity);
    }

    const { sellingPlan } = this.refs;
    sellingPlan.value = sellingPlanId;
    // A disabled control is left out of FormData, so one-time purchases send no selling_plan.
    sellingPlan.disabled = !sellingPlanId;
  }
}

if (!customElements.get('purchase-options-component')) {
  customElements.define('purchase-options-component', PurchaseOptionsComponent);
}

/**
 * Text button that adds the one-time purchase through the nearest purchase options on the page.
 *
 * @extends Component
 */
class OneTimePurchaseComponent extends Component {
  add() {
    const section = this.closest('.shopify-section') ?? document;
    const options = section.querySelector('purchase-options-component');
    const variantId = this.dataset.variantId ?? '';
    if (options instanceof PurchaseOptionsComponent) {
      options.addOneTime({ variantId });
      return;
    }
    // No option cards on the page: fall back to the product form directly.
    const form = section.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return;
    const input = form.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement && variantId) input.value = variantId;
    form.requestSubmit();
  }
}

if (!customElements.get('one-time-purchase-component')) {
  customElements.define('one-time-purchase-component', OneTimePurchaseComponent);
}
